import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import { toast } from "react-toastify";
import {
  deletePaymentPlatformFile,
  getCompanyUploadedFileDownloadUrl,
  getPaymentPlatformFiles,
  updatePaymentPlatformFileLabels,
} from "../api/companyApi";

export const DATA_FILE_LABELS = ["input", "metadata", "output"];
const labelColor = { input: "primary", metadata: "secondary", output: "success" };
const formatSize = (value) => {
  const size = Number(value || 0);
  if (!size) return "—";
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

export default function PaymentPlatformDataCenter({
  tenantId,
  refreshKey = 0,
  busy = false,
  onUpload,
  onMapColumns,
  onFilesLoaded,
  title = "Data center",
  description = "All tenant files in one place. A file can be input, metadata, output, or any combination.",
  uploadLabel = "Upload files",
  mapColumnsLabel = "Map columns",
}) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [labelFilter, setLabelFilter] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const data = await getPaymentPlatformFiles(tenantId);
      const items = data?.items || [];
      setFiles(items);
      onFilesLoaded?.(items);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not load the data center");
    } finally {
      setLoading(false);
    }
  }, [onFilesLoaded, tenantId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const visibleFiles = useMemo(() => {
    const query = search.trim().toLowerCase();
    return files.filter((file) => {
      if (labelFilter !== "all" && !(file.labels || []).includes(labelFilter)) return false;
      return !query || `${file.fileName} ${(file.labels || []).join(" ")} ${file.source || ""}`
        .toLowerCase()
        .includes(query);
    });
  }, [files, labelFilter, search]);

  const saveLabels = async (file, labels) => {
    if (!labels.length) {
      toast.error("Each file needs at least one label");
      return;
    }
    setSavingId(file.id);
    try {
      const updated = await updatePaymentPlatformFileLabels(tenantId, file.id, labels);
      setFiles((current) => current.map((item) => item.id === file.id ? { ...item, ...updated } : item));
      toast.success("File labels updated");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not update file labels");
    } finally {
      setSavingId(null);
    }
  };

  const download = async (file) => {
    try {
      const response = await getCompanyUploadedFileDownloadUrl(tenantId, file.fileGCSKey);
      window.open(response?.data?.downloadUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not download the file");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSavingId(deleteTarget.id);
    try {
      await deletePaymentPlatformFile(tenantId, deleteTarget.id);
      const remaining = files.filter((item) => item.id !== deleteTarget.id);
      setFiles(remaining);
      onFilesLoaded?.(remaining);
      setDeleteTarget(null);
      toast.success("File deleted");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not delete the file");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ borderColor: "#dce6f1", borderRadius: 3, overflow: "hidden", mb: 2 }}>
      <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ color: "#0b2a4a", fontWeight: 900, fontSize: 18 }}>
            {title}
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: 13 }}>
            {description}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading || busy}>Refresh</Button>
          {onMapColumns && (
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              disabled={!files.length || loading || busy}
              onClick={onMapColumns}
            >
              {mapColumnsLabel}
            </Button>
          )}
          {onUpload && (
            <Button
              variant="contained"
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={busy}
              onClick={onUpload}
            >
              {uploadLabel}
            </Button>
          )}
        </Box>
      </Box>
      <Box sx={{ px: 2, pb: 1.5, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 180px" }, gap: 1 }}>
        <TextField
          size="small"
          placeholder="Search files, labels or source…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <TextField select size="small" label="Label" value={labelFilter} onChange={(event) => setLabelFilter(event.target.value)}>
          <MenuItem value="all">All labels</MenuItem>
          {DATA_FILE_LABELS.map((label) => <MenuItem key={label} value={label}>{label}</MenuItem>)}
        </TextField>
      </Box>
      <TableContainer sx={{ maxHeight: 430, borderTop: "1px solid #e5edf5" }}>
        <Table stickyHeader size="small" aria-label="Tenant data files">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 900 }}>File</TableCell>
              <TableCell sx={{ fontWeight: 900, minWidth: 260 }}>Labels</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Size</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Added</TableCell>
              <TableCell sx={{ fontWeight: 900 }}>Source</TableCell>
              <TableCell align="right" sx={{ fontWeight: 900 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><CircularProgress size={24} /></TableCell></TableRow>
            )}
            {!loading && !visibleFiles.length && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#64748b" }}>
                  {files.length ? "No files match these filters." : "No files yet. Upload one or more files to get started."}
                </TableCell>
              </TableRow>
            )}
            {!loading && visibleFiles.map((file) => (
              <TableRow hover key={file.id}>
                <TableCell>
                  <Typography noWrap title={file.fileName} sx={{ fontWeight: 750, maxWidth: 330 }}>{file.fileName}</Typography>
                </TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth disabled={savingId === file.id}>
                    <InputLabel>Labels</InputLabel>
                    <Select
                      multiple
                      label="Labels"
                      value={file.labels || []}
                      onChange={(event) => saveLabels(file, event.target.value)}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {selected.map((label) => <Chip key={label} size="small" color={labelColor[label]} label={label} />)}
                        </Box>
                      )}
                    >
                      {DATA_FILE_LABELS.map((label) => (
                        <MenuItem key={label} value={label}>
                          <Checkbox checked={(file.labels || []).includes(label)} />
                          <ListItemText primary={label} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </TableCell>
                <TableCell>{formatSize(file.size)}</TableCell>
                <TableCell>{file.createdAt ? new Date(file.createdAt).toLocaleString() : "—"}</TableCell>
                <TableCell><Chip size="small" variant="outlined" label={(file.source || "upload").replaceAll("_", " ")} /></TableCell>
                <TableCell align="right">
                  <Tooltip title="Download"><IconButton aria-label={`Download ${file.fileName}`} onClick={() => download(file)}><DownloadOutlinedIcon /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton color="error" aria-label={`Delete ${file.fileName}`} onClick={() => setDeleteTarget(file)}><DeleteOutlineIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete file?</DialogTitle>
        <DialogContent>
          <Typography>
            This permanently removes <strong>{deleteTarget?.fileName}</strong> from tenant storage and future model runs. Completed assessment history is preserved.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={savingId === deleteTarget?.id} onClick={confirmDelete}>
            Delete file
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
